import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface SavedLink {
  id: string;
  link_type: string;
  label: string;
  url: string;
  created_at: string;
  updated_at: string;
}

export const useSavedLinks = () => {
  const { user } = useAuth();
  const [links, setLinks] = useState<SavedLink[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchLinks = async () => {
    if (!user) {
      setLinks([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("saved_links")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setLinks(data || []);
    } catch (error) {
      console.error("Error fetching saved links:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [user]);

  const addLink = async (linkType: string, label: string, url: string) => {
    if (!user) {
      toast.error("Please log in to save links");
      return null;
    }

    try {
      const { data, error } = await supabase
        .from("saved_links")
        .insert([{ user_id: user.id, link_type: linkType, label, url }])
        .select()
        .single();

      if (error) throw error;
      setLinks((prev) => [data, ...prev]);
      toast.success("Link saved!");
      return data;
    } catch (error) {
      console.error("Error saving link:", error);
      toast.error("Failed to save link");
      return null;
    }
  };

  const updateLink = async (id: string, updates: Partial<Pick<SavedLink, "label" | "url" | "link_type">>) => {
    try {
      const { error } = await supabase
        .from("saved_links")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
      setLinks((prev) =>
        prev.map((link) => (link.id === id ? { ...link, ...updates } : link))
      );
      toast.success("Link updated!");
    } catch (error) {
      console.error("Error updating link:", error);
      toast.error("Failed to update link");
    }
  };

  const deleteLink = async (id: string) => {
    try {
      const { error } = await supabase
        .from("saved_links")
        .delete()
        .eq("id", id);

      if (error) throw error;
      setLinks((prev) => prev.filter((link) => link.id !== id));
      toast.success("Link deleted!");
    } catch (error) {
      console.error("Error deleting link:", error);
      toast.error("Failed to delete link");
    }
  };

  const getLinksByType = (type: string) => {
    return links.filter((link) => link.link_type === type);
  };

  return {
    links,
    isLoading,
    addLink,
    updateLink,
    deleteLink,
    getLinksByType,
    refetch: fetchLinks,
  };
};
